import * as React from 'react';
import {Routes, Route, RouteProps, matchPath, useLocation, Navigate} from 'react-router-dom';

import Layout from 'components/layout';
import {PrivateRoute} from './PrivateRoute';

import Backpack from 'pages/backpack';
import Callback from 'pages/callback';
import Chat from 'pages/chat';
import Content from 'pages/content';
import Error from 'pages/error';
import Home from 'pages/home';
import Library from 'pages/library';
import Materials from 'pages/materials';
import UserSettings from 'pages/user-settings';
import Xpedition from 'pages/xpedition';
import Interventions from 'pages/intervention';
import {ProgramDetail} from 'pages/intervention/program-detail';
import {StepContent} from 'pages/intervention/step-content';
import Logout from 'pages/logout';
import Dialogues from 'pages/dialogues';
import Dialogue from '../dialogue';
import {XpeditionChat} from '../xpedition-chat/XpeditionChat';
import PublicContent from '../public-content';
import ArticlePage from '../article';
import {ArticleAuthors} from '../article-authors/ArticleAuthors';
import ArticleReferences from '../article-references';
import ArticleReference from '../article-reference';
import ArticleSection from '../article-section';
import ArticleSections from '../article-sections';
import {Resource} from '../resource/Resource';
import Journals from '../journals';
import JournalChat from '../../components/journal-chat';
import Reviews from '../reviews';
import ReviewChat from '../review-chat';
import Chatroom from '../chatroom';
import ChatroomChat from '../../components/chatroom-chat';
import CmResource from '../cm-resource';
import {CmResourceCard} from '../cm-resource-card/CmResourceCard';
import {Referrals} from '../referrals/Referrals';
import {CmResourceOrganization} from '../cm-resource-organization/CmResourceOrganization';
import {Search} from '../search/Search';
import {SearchChat} from '../../components/search-chat/SearchChat';
import Trainings from '../trainings';
import TrainingChat from '../../components/training-chat';
import Exercise from '../exercise';
import ExerciseChat from '../../components/exercise-chat';
import {AccessDenied} from '../access-denied/AccessDenied';
import {EmailNotVerified} from '../email-not-verified/EmailNotVerified';
import ArticleCollections from '../article-collections';
import ArticleTables from '../article-tables';
import ArticleTableOfContents from '../article-table-of-contents';
import ArticleSummary from '../article-summary';
import {StatisticsDashboard} from '../statistics-dashboard/StatisticsDashboard';

import './styles.scss';

import {Pages, RoutesUrl} from 'utils/constants/routeConstants';
import Loader from '../../components/loader';
import {useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import AllStatisticsDashboard from '../all-statistics-dashboard';
import Sessions from '../sessions';
import Activities from 'pages/activities';
import SessionChat from '../session-chat';
import ActivityChat from '../activity-chat';
import DmResource from '../dm-resource';
import Notifications from '../notifications';
import ActivityJournals from '../activity-journals';
import ActivityJournalChat from '../activity-journal-chat';

export type PageMeta = {
  visible: boolean;
  role?: string;
};

export type PagesMap = Record<string, PageMeta>;

type Props = RouteProps & {
  pages?: PagesMap;
};

type GateKind = 'public' | 'private' | 'gated';

type RouteDef = {
  key: string;
  path: string;
  element: React.ReactElement;
  gate: GateKind;
  page?: Pages;
};

const pageVisible = (pages?: PagesMap, page?: Pages) => {
  return Boolean(page && pages?.[page]?.visible);
};

const pageRole = (pages?: PagesMap, page?: Pages) => {
  return page ? pages?.[page]?.role : undefined;
};

const renderElement = (def: RouteDef, pages?: PagesMap) => {
  if (def.gate === 'public') {
    return <Layout>{def.element}</Layout>;
  }

  if (def.gate === 'private') {
    return <PrivateRoute>{def.element}</PrivateRoute>;
  }

  return (
    <PrivateRoute requiredRole={pageRole(pages, def.page)}>
      {def.element}
    </PrivateRoute>
  );
};

const ROUTES: RouteDef[] = [
  {key: 'home', path: RoutesUrl.Home, gate: 'public', element: <Home/>},
  {key: 'logout', path: RoutesUrl.Logout, gate: 'public', element: <Logout/>},
  {key: 'callback', path: RoutesUrl.Callback, gate: 'public', element: <Callback/>},
  {key: 'public-content', path: RoutesUrl.PublicContent, gate: 'public', element: <PublicContent/>},
  {key: 'access-denied', path: RoutesUrl.AccessDenied, gate: 'public', element: <AccessDenied/>},
  {key: 'email-not-verified', path: RoutesUrl.EmailNotVerified, gate: 'public', element: <EmailNotVerified/>},

  {key: 'xpedition-chat', path: RoutesUrl.Xpedition, gate: 'private', element: <XpeditionChat/>},
  {key: 'settings', path: RoutesUrl.Settings, gate: 'private', element: <UserSettings/>},
  {key: 'content', path: RoutesUrl.Content, gate: 'private', element: <Content/>},

  {key: 'article', path: RoutesUrl.Article, gate: 'private', element: <ArticlePage/>},
  {key: 'article-authors', path: RoutesUrl.ArticleAuthors, gate: 'private', element: <ArticleAuthors/>},
  {key: 'article-references', path: RoutesUrl.ArticleReferences, gate: 'private', element: <ArticleReferences/>},
  {key: 'article-reference', path: RoutesUrl.ArticleReference, gate: 'private', element: <ArticleReference/>},
  {key: 'reference-authors', path: RoutesUrl.ReferenceAuthors, gate: 'private', element: <ArticleAuthors/>},
  {key: 'article-section', path: RoutesUrl.ArticleSection, gate: 'private', element: <ArticleSection/>},
  {key: 'article-sections', path: RoutesUrl.ArticleSections, gate: 'private', element: <ArticleSections/>},
  {key: 'article-collections', path: RoutesUrl.ArticleCollections, gate: 'private', element: <ArticleCollections/>},
  {key: 'article-tables', path: RoutesUrl.ArticleTables, gate: 'private', element: <ArticleTables/>},
  {key: 'article-toc', path: RoutesUrl.ArticleTableOfContents, gate: 'private', element: <ArticleTableOfContents/>},
  {key: 'article-summary', path: RoutesUrl.ArticleSummary, gate: 'private', element: <ArticleSummary/>},

  {key: 'library', path: RoutesUrl.Library, gate: 'gated', page: Pages.Library, element: <Library/>},
  {key: 'materials', path: RoutesUrl.Materials, gate: 'gated', page: Pages.Share, element: <Materials/>},
  {key: 'backpack', path: RoutesUrl.Backpack, gate: 'gated', page: Pages.Backpack, element: <Backpack/>},

  {key: 'chat', path: RoutesUrl.Chat, gate: 'gated', page: Pages.Chat, element: <Chat/>},
  {key: 'chat-restart', path: RoutesUrl.ChatRestart, gate: 'gated', page: Pages.Chat, element: <Chat/>},

  {key: 'xpeditions', path: RoutesUrl.Xpeditions, gate: 'gated', page: Pages.Xpedition, element: <Xpedition/>},

  {key: 'interventions', path: RoutesUrl.Interventions, gate: 'gated', page: Pages.Intervention, element: <Interventions/>},
  {key: 'program-detail', path: RoutesUrl.ProgramDetail, gate: 'gated', page: Pages.Intervention, element: <ProgramDetail/>},
  {key: 'step-content', path: RoutesUrl.StepContent, gate: 'gated', page: Pages.Intervention, element: <StepContent/>},

  {key: 'dialogues', path: RoutesUrl.Dialogues, gate: 'gated', page: Pages.Dialogues, element: <Dialogues/>},
  {key: 'dialogue', path: RoutesUrl.Dialogue, gate: 'gated', page: Pages.Dialogues, element: <Dialogue/>},

  {key: 'resource', path: RoutesUrl.Resource, gate: 'gated', page: Pages.Resource, element: <Resource/>},

  {key: 'journals', path: RoutesUrl.Journals, gate: 'gated', page: Pages.Journals, element: <Journals/>},
  {key: 'journal', path: RoutesUrl.Journal, gate: 'gated', page: Pages.Journals, element: <JournalChat/>},

  {key: 'reviews', path: RoutesUrl.Reviews, gate: 'gated', page: Pages.Reviews, element: <Reviews/>},
  {key: 'review', path: RoutesUrl.Review, gate: 'gated', page: Pages.Reviews, element: <ReviewChat/>},

  {key: 'chatroom', path: RoutesUrl.Chatroom, gate: 'gated', page: Pages.Chatroom, element: <Chatroom/>},
  {
    key: 'chatroom-dialogue',
    path: RoutesUrl.ChatroomDialogue,
    gate: 'gated',
    page: Pages.Chatroom,
    element: <ChatroomChat/>
  },

  {key: 'cm-resource', path: RoutesUrl.CmResource, gate: 'gated', page: Pages.CmResource, element: <CmResource/>},
  {key: 'dm-resource', path: RoutesUrl.DmResource, gate: 'gated', page: Pages.DmResource, element: <DmResource/>},
  {
    key: 'cm-resource-card',
    path: RoutesUrl.CmResourceCard,
    gate: 'gated',
    page: Pages.CmResource,
    element: <CmResourceCard/>
  },
  {
    key: 'cm-resource-org',
    path: RoutesUrl.CmResourceOrganization,
    gate: 'gated',
    page: Pages.CmResource,
    element: <CmResourceOrganization/>
  },
  {
    key: 'dm-resource-card',
    path: RoutesUrl.CmResourceCard,
    gate: 'gated',
    page: Pages.DmResource,
    element: <CmResourceCard/>
  },
  {
    key: 'dm-resource-org',
    path: RoutesUrl.CmResourceOrganization,
    gate: 'gated',
    page: Pages.DmResource,
    element: <CmResourceOrganization/>
  },

  {key: 'referrals', path: RoutesUrl.Referrals, gate: 'gated', page: Pages.Referrals, element: <Referrals/>},

  {key: 'search', path: RoutesUrl.Search, gate: 'gated', page: Pages.Search, element: <Search/>},
  {key: 'search-dialogue', path: RoutesUrl.SearchDialogue, gate: 'gated', page: Pages.Search, element: <SearchChat/>},

  {key: 'trainings', path: RoutesUrl.Trainings, gate: 'gated', page: Pages.Trainings, element: <Trainings/>},
  {
    key: 'trainings-chat',
    path: RoutesUrl.TrainingsChat,
    gate: 'gated',
    page: Pages.Trainings,
    element: <TrainingChat/>
  },

  {key: 'exercise', path: RoutesUrl.Exercise, gate: 'gated', page: Pages.Exercises, element: <Exercise/>},
  {key: 'exercise-chat', path: RoutesUrl.ExerciseChat, gate: 'gated', page: Pages.Exercises, element: <ExerciseChat/>},

  {
    key: 'statistics',
    path: RoutesUrl.Statistics,
    gate: 'gated',
    page: Pages.Statistics,
    element: <StatisticsDashboard/>
  },

  {
    key: 'all-statistics',
    path: RoutesUrl.AllStatistics,
    gate: 'gated',
    page: Pages.AllStatistics,
    element: <AllStatisticsDashboard/>
  },

  {
    key: 'sessions',
    path: RoutesUrl.Sessions,
    gate: 'gated',
    page: Pages.Sessions,
    element: <Sessions/>
  },
  {key: 'session-chat', path: RoutesUrl.SessionChat, gate: 'gated', page: Pages.Sessions, element: <SessionChat/>},
  {
    key: 'activities',
    path: RoutesUrl.Activities,
    gate: 'gated',
    page: Pages.Activities,
    element: <Activities/>
  },
  {key: 'activity-chat', path: RoutesUrl.ActivityChat, gate: 'gated', page: Pages.Activities, element: <ActivityChat/>},
  {key: 'notifications', path: RoutesUrl.Notifications, gate: 'private', element: <Notifications/>},
  {
    key: 'activity-journals',
    path: RoutesUrl.ActivityJournals,
    gate: 'gated',
    page: Pages.ActivityJournals,
    element: <ActivityJournals/>
  },
  {key: 'activity-journal-chat', path: RoutesUrl.ActivityJournalChat, gate: 'gated', page: Pages.ActivityJournals, element: <ActivityJournalChat/>}
];

export const AppRoutes: React.FunctionComponent<Props> = ({pages}) => {
  const pagesReady = pages != null;

  const {auth0User} = useSelector((state: IStore) => state.auth);
  const location = useLocation();
  const isAuthed = Boolean(auth0User);

  const routes = React.useMemo(
    () => ROUTES.filter(def => def.gate !== 'gated' || pageVisible(pages, def.page)),
    [pages]
  );

  const isKnownGatedPath = React.useMemo(() => {
    return ROUTES
      .filter(r => r.gate === 'gated')
      .some(r => matchPath({path: r.path, end: true}, location.pathname));
  }, [location.pathname]);

  const renderFallback = () => {
    if (!isAuthed && isKnownGatedPath) {
      return (
        <Navigate
          to={RoutesUrl.Home}
          replace
          state={{from: location}}
        />
      );
    }

    if (!pagesReady && isKnownGatedPath) {
      return <Loader className="app-loader" />;
    }

    return <Error />;
  };

  return (
    <Routes>
      {routes.map(def => (
        <Route
          key={def.key}
          path={def.path}
          element={
            def.key === 'home'
              ? <Layout hideSeparator>{def.element}</Layout>
              : renderElement(def, pages)
          }
        />
      ))}
      <Route
        path="*"
        element={
          <Layout>
            {renderFallback()}
          </Layout>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
